// src/pages/VerifyResetCodePage.tsx
import React, { useState } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Button,
  TextField,
  Link,
  Box,
  Typography,
  Container,
  Alert,
  InputAdornment,
  AppBar,
  Toolbar,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PinOutlinedIcon from '@mui/icons-material/PinOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import authService from '../services/authService';
import { useTranslation } from 'react-i18next';

const VerifyResetCodePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || '';
  const theme = useTheme();

  const [resetCode, setResetCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const getErrorMessage = (err: any): string => {
    const data = err?.response?.data;
    if (!data) {
      if (err?.message) return err.message;
      return t('auth.errors.connection');
    }

    if (Array.isArray(data.detail)) {
      const messages = data.detail.map((e: any) => e.msg || e.message || JSON.stringify(e)).filter(Boolean);
      if (messages.length > 0) return messages.join('. ');
    }
    if (typeof data.detail === 'string') return data.detail;
    if (data.detail?.msg) return data.detail.msg;
    if (data.message) return data.message;
    if (data.error) return data.error;
    return t('auth.verifyCode.failed');
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);
    try {
      if (!email) {
        setError(t('auth.verifyCode.missing'));
        return;
      }
      await authService.verifyResetCode({ email, reset_code: resetCode });
      setSuccess(t('auth.verifyCode.success'));
      navigate('/reset-password', { state: { email, resetCode } });
    } catch (err: any) {
      setError(getErrorMessage(err));
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(135deg, #e8e0f0 0%, #f0e8f5 50%, #f5f0fa 100%)',
      }}
    >
      {/* Minimal Navbar */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          background: 'transparent',
          borderBottom: '1px solid rgba(0,0,0,0.05)',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Link
            component={RouterLink}
            to="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              gap: 1,
            }}
          >
            <Box
              component="img"
              src="/logo512.png"
              alt="Zemen Logo"
              sx={{ height: 36, width: 36 }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
              }}
            >
              {t('auth.brand')}
            </Typography>
          </Link>
          <Link
            component={RouterLink}
            to="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              color: 'text.secondary',
              textDecoration: 'none',
              '&:hover': { color: 'primary.main' },
            }}
          >
            <HomeOutlinedIcon fontSize="small" />
            <Typography variant="body2">{t('auth.backHome')}</Typography>
          </Link>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4,
        }}
      >
        <Container maxWidth="sm">
          <Box
            sx={{
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(20px)',
              borderRadius: 4,
              p: { xs: 4, md: 6 },
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
            }}
          >
            {/* Logo */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box
                component="img"
                src="/logo512.png"
                alt="Zemen Logo"
                sx={{ height: 60, width: 60, mb: 2 }}
              />
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {t('auth.verifyCode.title')}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                {t('auth.verifyCode.subtitle', { email: email || 'your email' })}
              </Typography>
            </Box>

            {/* Form */}
            <Box component="form" onSubmit={handleSubmit} noValidate>
              {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                  {error}
                </Alert>
              )}
              {success && (
                <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
                  {success}
                </Alert>
              )}

              <TextField
                margin="normal"
                required
                fullWidth
                id="resetCode"
                label={t('auth.verifyCode.codeLabel')}
                name="resetCode"
                autoComplete="off"
                autoFocus
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PinOutlinedIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255,255,255,0.8)',
                  },
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  mt: 4,
                  mb: 2,
                  py: 1.5,
                  borderRadius: 2,
                  fontSize: '1rem',
                  fontWeight: 600,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  boxShadow: `0 8px 24px ${theme.palette.primary.main}40`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                    boxShadow: `0 12px 32px ${theme.palette.primary.main}50`,
                  },
                }}
              >
                {t('auth.verifyCode.submit')}
              </Button>

              <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Link
                  component={RouterLink}
                  to="/forgot-password"
                  sx={{
                    color: 'text.secondary',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    '&:hover': { color: 'primary.main' },
                  }}
                >
                  <ArrowBackIcon fontSize="small" />
                  {t('auth.verifyCode.resend')}
                </Link>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default VerifyResetCodePage;
